/**
 * One run of ประลองยุทธภพ as plain data and pure functions (C0-TOURNEY): the field, the Swiss rounds, the knock-out,
 * the places. No World here: `systems/tournament.ts` brings the clock, the ring and the player's real bouts. Every
 * draw is the run's own seeded stream (`seed` and a step counter that is saved with the run), so a run can be saved,
 * loaded and resumed, and the same seed always plays out the same.
 *
 * The player (entrant `me`, 0 for none) fights their own bouts for real; every other pair is decided here by the
 * entrants' ratings (Elo, then best of three) and the stream. `settle` records a result and moves on whenever a round is
 * complete.
 */
import {
  advanceBracket,
  bracketPairs,
  newSwiss,
  pairRound,
  reportResult,
  standings,
  topCut,
  type SwissEntrant,
  type SwissRow,
  type SwissState,
} from '../swiss';
import { mulberry32, type Rng } from '../rng';
import { TOURNEY_MODES, type TourneyModeId } from '../data/tournament';

export type EntrantKind = 'player' | 'wanderer' | 'spar' | 'guest';

export interface TourneyEntrant {
  readonly id: number;
  readonly name: string;
  readonly kind: EntrantKind;
  /** Index into the wanderers (kind 'wanderer'), the sparring tiers (kind 'spar'), else 0. */
  readonly idx: number;
  readonly rating: number;
  /** The manner (a `DUEL_STYLES` id) and skill (0-4) the rival's mind fights with, and the sprite. */
  readonly style: string;
  readonly level: number;
  readonly sprite: string;
  readonly guild?: string;
}

/** A bout: `w` is the winner's id, null while it is still to be fought. `b` null is a bye. */
export interface BoutRec {
  readonly a: number;
  readonly b: number | null;
  w: number | null;
}

export type RunPhase = 'swiss' | 'swissDone' | 'bracket' | 'done';

export interface TourneyRun {
  readonly mode: TourneyModeId;
  readonly week: number;
  readonly seed: number;
  /** Draws made so far from the run's stream. */
  step: number;
  phase: RunPhase;
  /** The player's entrant id, or 0 when the player is not in the field. */
  readonly me: number;
  readonly entrants: TourneyEntrant[];
  swiss: SwissState;
  /** The Swiss round under way, and the rounds finished. */
  pairs: BoutRec[];
  history: BoutRec[][];
  /** Knock-out rounds, the last one the current. */
  bracket: BoutRec[][];
  /** Once done: the ids from first place down (cut length). */
  places: number[];
  /** The Swiss wins of each entrant at the end (for coins and the hall). */
  finalScores: Record<number, number>;
  /** The rewards have been paid. */
  paid?: boolean;
}

export const entrantOf = (run: TourneyRun, id: number): TourneyEntrant => {
  const e = run.entrants.find((x) => x.id === id);
  if (!e) throw new Error(`tournament: no entrant ${id}`);
  return e;
};

/** The run's own draw: deterministic, resumable. */
function draw(run: TourneyRun): number {
  return mulberry32((run.seed + Math.imul(run.step++, 7919)) | 0)();
}
const rngOf = (run: TourneyRun): Rng => () => draw(run);

/** Chance that `a` wins a best-of-three over `b`: Elo for a round, then the best-of-three. */
export function boutChance(ra: number, rb: number): number {
  const p = 1 / (1 + 10 ** ((rb - ra) / 400));
  return p * p * (3 - 2 * p);
}

/** Starts a run: Swiss round one paired, every pair that does not hold the player decided. */
export function makeRun(opts: { mode: TourneyModeId; week: number; seed: number; me: number; entrants: TourneyEntrant[] }): TourneyRun {
  const m = TOURNEY_MODES[opts.mode];
  const swissEntrants: SwissEntrant[] = opts.entrants.map((e) => ({ id: e.id, rating: e.rating, guild: e.guild }));
  const run: TourneyRun = {
    mode: opts.mode,
    week: opts.week,
    seed: opts.seed | 0,
    step: 0,
    phase: 'swiss',
    me: opts.me,
    entrants: opts.entrants,
    swiss: newSwiss(swissEntrants, m.rounds),
    pairs: [],
    history: [],
    bracket: [],
    places: [],
    finalScores: {},
  };
  nextSwissRound(run);
  return run;
}

function decide(run: TourneyRun, rec: BoutRec): void {
  if (rec.w !== null || rec.b === null) return;
  if (run.me && (rec.a === run.me || rec.b === run.me)) return;
  const a = entrantOf(run, rec.a);
  const b = entrantOf(run, rec.b);
  rec.w = draw(run) < boutChance(a.rating, b.rating) ? a.id : b.id;
}

function nextSwissRound(run: TourneyRun): void {
  const ps = pairRound(run.swiss, rngOf(run));
  run.pairs = ps.map((p) => ({ a: p.a, b: p.b, w: p.b === null ? p.a : null }));
  for (const r of run.pairs) decide(run, r);
  settleRound(run);
}

/** The pair that still waits for the player's bout, or null. */
export function pendingFor(run: TourneyRun, id: number): BoutRec | null {
  if (!id) return null;
  const list = run.phase === 'swiss' ? run.pairs : run.phase === 'bracket' ? (run.bracket[run.bracket.length - 1] ?? []) : [];
  return list.find((r) => r.w === null && (r.a === id || r.b === id)) ?? null;
}

/** Records the winner of a pair (a bout the player fought, or a forfeit) and moves the run on if the round is complete. */
export function settle(run: TourneyRun, rec: BoutRec, winner: number): void {
  if (rec.w !== null) return;
  if (winner !== rec.a && winner !== rec.b) throw new Error('tournament: the winner is not one of the pair');
  rec.w = winner;
  settleRound(run);
}

function settleRound(run: TourneyRun): void {
  for (let guard = 0; guard < 50; guard++) {
    if (run.phase === 'swiss') {
      if (run.pairs.some((r) => r.w === null)) return;
      for (const r of run.pairs) if (r.b !== null) reportResult(run.swiss, r.a, r.b, r.w!);
      run.history.push(run.pairs);
      if (run.swiss.round < run.swiss.rounds) {
        const ps = pairRound(run.swiss, rngOf(run));
        run.pairs = ps.map((p) => ({ a: p.a, b: p.b, w: p.b === null ? p.a : null }));
        for (const r of run.pairs) decide(run, r);
        continue;
      }
      run.pairs = [];
      for (const p of run.swiss.players) run.finalScores[p.id] = p.score;
      run.phase = 'swissDone';
      return;
    }
    if (run.phase === 'bracket') {
      const cur = run.bracket[run.bracket.length - 1]!;
      if (cur.some((r) => r.w === null)) return;
      if (cur.length === 1) return finish(run);
      const next = advanceBracket(cur.map((r) => r.w!));
      run.bracket.push(next.map(([a, b]) => ({ a, b, w: null })));
      for (const r of run.bracket[run.bracket.length - 1]!) decide(run, r);
      continue;
    }
    return;
  }
}

/** The knock-out of the top of the Swiss (call when the Swiss is over and its time has come). */
export function startBracket(run: TourneyRun): void {
  if (run.phase !== 'swissDone') return;
  const cut = TOURNEY_MODES[run.mode].cut;
  const seeds = topCut(run.swiss, cut);
  run.phase = 'bracket';
  run.bracket = [bracketPairs(seeds).map(([a, b]) => ({ a, b, w: null }))];
  for (const r of run.bracket[0]!) decide(run, r);
  settleRound(run);
}

function finish(run: TourneyRun): void {
  const final = run.bracket[run.bracket.length - 1]![0]!;
  const champion = final.w!;
  const second = final.a === champion ? final.b! : final.a;
  const rank = new Map(standings(run.swiss).map((r) => [r.id, r.rank] as const));
  const semi = run.bracket.length >= 2 ? run.bracket[run.bracket.length - 2]! : [];
  const lost = semi.flatMap((r) => (r.b === null ? [] : [r.a === r.w ? r.b : r.a])).sort((x, y) => rank.get(x)! - rank.get(y)!);
  run.places = [champion, second, ...lost];
  run.phase = 'done';
}

/** The player gives the rest away (or never turns up): every pair of theirs is lost, and the run goes on to its end of this stage. */
export function forfeitMine(run: TourneyRun): void {
  for (let guard = 0; guard < 30; guard++) {
    const rec = pendingFor(run, run.me);
    if (!rec) return;
    settle(run, rec, rec.a === run.me ? rec.b! : rec.a);
  }
}

/** Decides everything left (the player forfeits), through the knock-out when `bracketToo`. */
export function resolveAll(run: TourneyRun, bracketToo: boolean): void {
  forfeitMine(run);
  if (bracketToo && run.phase === 'swissDone') startBracket(run);
  forfeitMine(run);
}

export const swissStandings = (run: TourneyRun): SwissRow[] => standings(run.swiss);

/** Swiss wins of an entrant so far (byes included). */
export const scoreOf = (run: TourneyRun, id: number): number => run.swiss.players.find((p) => p.id === id)?.score ?? 0;

/** Where an entrant finished in the knock-out (1-based), or 0 when out of it. */
export const placeOf = (run: TourneyRun, id: number): number => run.places.indexOf(id) + 1;
